import { DocumentWithDownloadUrl as Document } from '@/client';
import { displayRecordName, getTypeTag } from '@/utils/documentUtils';
import { makeLogger } from '@/utils/logger/logger.ts';

const logger = makeLogger('useUpdateTextRecord');
import { useUpdateDocument } from './mutations/useUpdateDocument';
import { UpdateDocumentTaskMetadata } from '@/types/tasks';
import { useCurrentHouse } from '@/context/CurrentHouseContext.tsx';
import { useHouseTaskManager } from '@/context/house/tasks/HouseTaskManagerContext';
import { CONCURRENT_TASK_TYPES } from '@/context/house/tasks/types';
import { useQueryClient } from '@tanstack/react-query';
import { houseDocumentStatusKeys } from './queries/queryKeys';
import { registerDocumentIdSwap } from './useUploadedRecords';

interface UseUpdateTextRecordOptions<T> {
	document: Document;
	transformData: (data: T) => string;
}

/**
 * Hook for updating text-based document records using HTMC (V3 document-centric architecture).
 * Schedules an UPDATE_RECORD task with concurrency control and retry logic.
 * HTMC reconciles completion when the house-level status shows uploaded_at for the updated document.
 * Optimistic updates are handled by useUpdateDocument's onMutate callback.
 */
export const useUpdateTextRecord = <T>(props: UseUpdateTextRecordOptions<T>) => {
	const { document, transformData } = props;
	const { house } = useCurrentHouse();
	const { scheduleTaskBatch, updateApiTaskMetadata } = useHouseTaskManager();
	const { updateDocumentMutation: updateSingleDocument } = useUpdateDocument(house.id);
	const queryClient = useQueryClient();

	const updateDocument = async (data: T) => {
		const description = transformData(data);
		const documentName = displayRecordName(document);

		const tag = getTypeTag(document);
		const tags = tag ? [tag] : [];
		const metadata: UpdateDocumentTaskMetadata = {
			documentId: document.id,
			filename: documentName,
			houseId: house.id,
			tags,
		};

		// Optimistically clear uploaded_at in status cache BEFORE scheduling task
		// This prevents HTMC reconciliation from completing the task prematurely
		const statusQueryKey = houseDocumentStatusKeys.status(house.id);
		await queryClient.cancelQueries({ queryKey: statusQueryKey });

		queryClient.setQueryData(statusQueryKey, (old) => {
			if (!old?.documents) return old;
			return {
				...old,
				documents: old.documents.map((doc) => {
					if (doc.id === document.id) {
						return {
							...doc,
							uploaded_at: null,
							upload_failed_at: null,
							upload_error: null,
							bnd: null,
							kb_sync: null,
						};
					}
					return doc;
				}),
			};
		});

		// Create a unique id for this update attempt for de-dupe safety
		const taskId = `update-${house.id}-${document.id}-${Date.now()}`;

		await scheduleTaskBatch(
			[
				{
					id: taskId,
					taskType: CONCURRENT_TASK_TYPES.UPDATE_RECORD,
					metadata,
					executeFn: async () => {
						// V3: mutation returns object with newId, previousDocumentId, and documentId (string)
						const response = await updateSingleDocument({
							houseId: house.id,
							documentId: document.id,
							description,
							tags,
						});

						// Handle document ID transition if API returned a new ID
						if (response.previousDocumentId && response.newId !== response.previousDocumentId) {
							logger.debug('Document ID changed:', response.previousDocumentId, '→', response.newId);

							// Register the ID swap for stable React keys
							registerDocumentIdSwap(response.previousDocumentId, response.newId);

							// Update task metadata with new document ID
							updateApiTaskMetadata(taskId, {
								documentId: response.newId,
								previousDocumentId: response.previousDocumentId,
							});
						}

						// Return string documentId for HTMC contract
						return response.documentId;
					},
				},
			],
			{},
		);
	};

	return {
		updateDocument,
		isUpdating: false,
	};
};

export default useUpdateTextRecord;
